import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import {
  AccountReportFilterActions,
  AccountReportPageHeader,
  AccountReportTableShell,
} from "@/components/accounts/AccountReportShell";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { formatReportDate, money } from "@/lib/accounts/ledgerReportUtils";
import {
  downloadIncomeStatementExcel,
  downloadIncomeStatementPdf,
  fetchIncomeStatement,
  type IncomeStatement,
} from "@/lib/accounts/incomeStatementStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { TrendingUp } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

function amountText(amount: number): string {
  const formatted = money(amount);
  return amount < 0 ? `(${formatted})` : formatted;
}

const IncomeStatementPage = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");

  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [report, setReport] = useState<IncomeStatement | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hideZero, setHideZero] = useState(true);

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";

  const activeFilters = useMemo(
    () => ({
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
      hideZero,
    }),
    [dateFrom, dateTo, hideZero],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  const applyFilters = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchIncomeStatement(activeFilters);
      setReport(data);
      setHasApplied(true);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[IncomeStatement] Load failed", error);
      toast.error("Could not load income statement.");
    } finally {
      setLoading(false);
    }
  }, [canView, activeFilters]);

  const exportReport = useCallback(
    async (format: "pdf" | "excel") => {
      if (!hasApplied || exporting) return;
      setExporting(format);
      try {
        if (format === "pdf") {
          await downloadIncomeStatementPdf(activeFilters);
          toast.success("PDF downloaded.");
        } else {
          await downloadIncomeStatementExcel(activeFilters);
          toast.success("Excel downloaded.");
        }
      } catch (error) {
        console.error(`[IncomeStatement] ${format} export failed`, error);
        toast.error(format === "pdf" ? "Could not download PDF." : "Could not download Excel.");
      } finally {
        setExporting(null);
      }
    },
    [hasApplied, exporting, activeFilters],
  );

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setHideZero(true);
    setReport(null);
    setHasApplied(false);
  };

  const hasAnyLines = useMemo(() => {
    if (!report) return false;
    return report.sections.some((section) => section.lines.length > 0);
  }, [report]);

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <AccountReportPageHeader
          icon={<TrendingUp className="h-5 w-5 text-primary" />}
          title="Income Statement"
          description="Profit and loss for a period: income, cost of sales, gross profit, expenses, and net income."
        />

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">From date</Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="All dates (optional)"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">To date</Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="All dates (optional)"
                align="end"
              />
            </div>

            <div className="flex items-end gap-2 pb-1 sm:col-span-2">
              <Switch id="hide-zero-amounts" checked={hideZero} onCheckedChange={setHideZero} />
              <Label htmlFor="hide-zero-amounts" className="cursor-pointer text-xs font-medium text-foreground">
                Hide zero-amount accounts
              </Label>
            </div>
          </div>

          <AccountReportFilterActions
            hasApplied={hasApplied}
            loading={loading}
            exporting={exporting}
            onApply={() => void applyFilters()}
            onReset={resetFilters}
            onExport={(format) => void exportReport(format)}
            statusText={
              hasApplied ? (
                <>
                  Income statement
                  {dateFrom || dateTo ? (
                    <>
                      {" · "}
                      {dateFrom ? (
                        <>
                          From <span className="font-semibold text-foreground">{formatReportDate(dateFrom)}</span>
                        </>
                      ) : (
                        "From start"
                      )}
                      {" · "}
                      {dateTo ? (
                        <>
                          To <span className="font-semibold text-foreground">{formatReportDate(dateTo)}</span>
                        </>
                      ) : (
                        "To latest"
                      )}
                    </>
                  ) : (
                    " · All posted dates"
                  )}
                  {report ? (
                    <>
                      {" · Net "}
                      <span
                        className={cn(
                          "font-semibold",
                          report.totals.net_income >= 0
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-rose-600 dark:text-rose-400",
                        )}
                      >
                        {amountText(report.totals.net_income)}
                      </span>
                    </>
                  ) : null}
                </>
              ) : (
                "Choose date range (optional), then click Apply filter."
              )
            }
          />
        </div>

        <AccountReportTableShell
          loading={loading}
          hasApplied={hasApplied}
          notAppliedMessage="No income statement loaded yet. Apply a filter to view profit and loss."
        >
          {!report || !hasAnyLines ? (
            <p className="py-12 text-center text-sm text-muted-foreground">No income or expense activity for this filter.</p>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Amount{currencySymbol ? ` (${currencySymbol})` : ""}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.sections.map((section) => (
                    <Fragment key={section.key}>
                      <TableRow className="bg-muted/20 hover:bg-muted/20">
                        <TableCell colSpan={3} className="py-2 text-xs font-bold uppercase tracking-wide text-primary/80">
                          {section.label}
                        </TableCell>
                      </TableRow>
                      {section.lines.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-sm text-muted-foreground">
                            No accounts in this section.
                          </TableCell>
                        </TableRow>
                      ) : (
                        section.lines.map((line) => (
                          <TableRow key={line.account_id}>
                            <TableCell className="font-mono text-xs text-muted-foreground">{line.account_code || "—"}</TableCell>
                            <TableCell className="min-w-[10rem] font-medium">{line.account_name}</TableCell>
                            <TableCell
                              className={cn(
                                "text-right tabular-nums",
                                line.amount >= 0
                                  ? "text-emerald-700 dark:text-emerald-400"
                                  : "text-rose-700 dark:text-rose-400",
                              )}
                            >
                              {amountText(line.amount)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      <TableRow className="bg-muted/10 hover:bg-muted/10">
                        <TableCell colSpan={2} className="font-semibold text-foreground">
                          Total {section.label}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{amountText(section.total)}</TableCell>
                      </TableRow>
                      {section.key === "cos" ? (
                        <TableRow className="bg-primary/5 hover:bg-primary/5">
                          <TableCell colSpan={2} className="font-semibold text-foreground">
                            Gross Profit
                          </TableCell>
                          <TableCell
                            className={cn(
                              "text-right tabular-nums font-semibold",
                              report.totals.gross_profit >= 0
                                ? "text-emerald-700 dark:text-emerald-400"
                                : "text-rose-700 dark:text-rose-400",
                            )}
                          >
                            {amountText(report.totals.gross_profit)}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  ))}
                  <TableRow className="bg-emerald-500/10 hover:bg-emerald-500/10">
                    <TableCell colSpan={2} className="font-semibold text-foreground">
                      Net Income
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums font-semibold",
                        report.totals.net_income >= 0
                          ? "text-emerald-700 dark:text-emerald-400"
                          : "text-rose-700 dark:text-rose-400",
                      )}
                    >
                      {amountText(report.totals.net_income)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </AccountReportTableShell>
      </div>
    </Layout>
  );
};

export default IncomeStatementPage;
